/**
 * Compute the list of supporting documents the advisor wants the client
 * to upload, based on the boxes they checked in the situation-update
 * sections of the wizard.
 *
 * @param {object} formData - The full wizard formData object.
 * @returns {string[]}      - Human-friendly list of requested documents.
 */
export function computeRequestedDocuments(formData) {
  const docs = [];

  const family = formData.family_personal || {};
  if (family.divorced) docs.push('Divorce agreement / decree');
  if (family.moved && family.new_address) docs.push('New address confirmation (lease, utility bill, etc.)');

  const career = formData.career_income || {};
  if (career.changed_jobs) {
    docs.push('Employer handbook / benefits elections');
    docs.push('Recent paystub from new employer');
    docs.push('New employer retirement account statement (if not managed by HFF)');
  }
  if (career.promoted) {
    docs.push('Updated benefits elections / paystub reflecting comp change');
  }
  if (career.retired) {
    docs.push('Retiree package or pension/severance statements');
  }
  if (career.started_or_sold_business) {
    docs.push('Business sale, K-1, or formation documents');
  }

  const fin = formData.financial_planning || {};
  if (fin.new_or_consolidated_accounts) {
    docs.push('Statement(s) for any new, consolidated, or transferred accounts');
  }
  if (fin.large_purchase_or_sale) {
    docs.push('Closing statement / details of major purchase or sale');
  }
  if (fin.insurance_changes) {
    docs.push('New or updated insurance policy documents');
  }
  if (fin.estate_changes) {
    docs.push('Updated estate planning / beneficiary documents');
  }
  if (fin.inheritance_or_gift) {
    docs.push('Inheritance or large gift details / statements');
  }

  // Income triggers
  const income = formData.income || {};
  for (const src of income.sources || []) {
    if (!src || !src.type) continue;
    const t = String(src.type).toLowerCase();
    if (t.includes('salary')) docs.push('Recent pay stub');
    if (t.includes('social security')) docs.push('Social Security statement');
    if (t.includes('pension')) docs.push('Pension statement');
    if (t.includes('annuity')) docs.push('Annuity statement');
    if (t.includes('rental')) docs.push('Rental income statement / Schedule E');
    if (t.includes('k-1')) docs.push('Most recent K-1');
  }

  // New debts marked as new since last meeting
  const debts = formData.debts || {};
  for (const entry of debts.entries || []) {
    if (!entry || entry.is_new !== 'yes' || !entry.type) continue;
    const t = String(entry.type).toLowerCase();
    const institution = entry.institution ? ` (${entry.institution})` : '';
    if (t.includes('mortgage') || t.includes('2nd mortgage')) {
      docs.push(`Mortgage closing statement / loan summary${institution}`);
    } else if (t.includes('heloc')) {
      docs.push(`HELOC agreement / statement${institution}`);
    } else if (t.includes('auto')) {
      docs.push(`Auto loan statement${institution}`);
    } else if (t.includes('student')) {
      docs.push(`Student loan statement${institution}`);
    } else if (t.includes('personal')) {
      docs.push(`Personal loan / line of credit statement${institution}`);
    } else {
      docs.push(`New ${entry.type} statement${institution}`);
    }
  }

  // De-duplicate while preserving order
  return Array.from(new Set(docs));
}
